import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import ElementChart from './ElementChart';
import QuestionDetails from './QuestionDetails';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLayout, FiTarget, FiUsers, FiClock, FiLayers } = FiIcons;

const Dashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  
  // Data State
  const [activeElements, setActiveElements] = useState([]);
  const [clusteredElements, setClusteredElements] = useState([]); // Array of { ...cluster, elements: [] }
  const [allQuestions, setAllQuestions] = useState({}); // Map: elementId -> [questions]
  const [questionScores, setQuestionScores] = useState({}); // Map: questionId -> score
  const [elementScores, setElementScores] = useState({}); // Map: elementId -> score
  
  // UI State
  const [selectedElement, setSelectedElement] = useState(null);
  const [activeClusterId, setActiveClusterId] = useState(null); // State für aktiven Tab

  useEffect(() => {
    if (user?.teamId) {
      loadDashboardData();
    }
  }, [user]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // 1. Initial Fetch: Team context data & Clusters (Parallel)
      const [
        teamMembersResponse, 
        teamUsesElementResponse, 
        clustersResponse,
        clusterRelationsResponse
      ] = await Promise.all([
        apiService.getUserTeamRelations({ team_id: user.teamId, limit: 1000 }),
        apiService.getTeamUsesElement({ team_id: user.teamId, is_used: 1, limit: 1000 }),
        apiService.getClusters(),
        apiService.getClusterElementRelations()
      ]);

      // --- Process Members ---
      const members = teamMembersResponse.data 
        ? teamMembersResponse.data.map(m => m.user_id) 
        : [];
      if (user.id && !members.includes(parseInt(user.id))) {
        members.push(parseInt(user.id));
      }

      // --- Process Active Elements ---
      if (!teamUsesElementResponse.data || teamUsesElementResponse.data.length === 0) {
        setLoading(false);
        setActiveElements([]);
        setClusteredElements([]);
        return;
      }

      const activeElementIds = teamUsesElementResponse.data.map(r => r.element_id);

      // 2. Dependent Fetch: Specific data based on IDs (Parallel)
      const promises = [
        // Fetch only active elements
        activeElementIds.length > 0 
          ? apiService.getElements({ 'id[in]': activeElementIds.join(',') })
          : Promise.resolve({ data: [] }),
          
        // Fetch questions only for active elements
        activeElementIds.length > 0 
          ? apiService.getQuestions({ 'element_id[in]': activeElementIds.join(','), limit: 1000 })
          : Promise.resolve({ data: [] }),
          
        // Fetch evaluations for all team members
        members.length > 0
          ? apiService.getEvaluations({ 'user_id[in]': members.join(','), limit: 2000 })
          : Promise.resolve({ data: [] })
      ];

      const [
        elementsResponse, 
        questionsResponse,
        evaluationsResponse
      ] = await Promise.all(promises);

      // --- Process Elements ---
      const validElements = elementsResponse.data || [];
      setActiveElements(validElements);

      // --- Process Clusters & Grouping ---
      const clusters = clustersResponse.data || [];
      const clusterRelations = clusterRelationsResponse.data || [];
      
      // Initialize clusters map with empty elements array
      const clustersMap = {};
      clusters.forEach(c => {
         clustersMap[c.id] = { ...c, elements: [] };
      });
      
      const noClusterElements = [];
      
      // Distribute elements to clusters
      validElements.forEach(el => {
         const rel = clusterRelations.find(r => r.element_id === el.id);
         if (rel && clustersMap[rel.cluster_id]) {
            clustersMap[rel.cluster_id].elements.push(el);
         } else {
            noClusterElements.push(el);
         }
      });
      
      // Convert to array and filter out empty clusters
      const groupedData = Object.values(clustersMap).filter(c => c.elements.length > 0);
      
      // Add "No Cluster" group if needed
      if (noClusterElements.length > 0) {
         groupedData.push({ 
           id: 'none', 
           title: 'Allgemein / Ohne Cluster', 
           description: 'Elemente ohne spezifische Cluster-Zuordnung',
           elements: noClusterElements 
         });
      }
      
      setClusteredElements(groupedData);
      
      // Set initial active cluster AND initial selected element
      if (groupedData.length > 0) {
        setActiveClusterId(groupedData[0].id);
        
        // Wähle das erste Element des ersten Clusters aus
        if (groupedData[0].elements && groupedData[0].elements.length > 0) {
          setSelectedElement(groupedData[0].elements[0]);
        }
      }

      // --- Process Questions ---
      const questionsMap = {};
      const relevantQuestionIds = new Set();
      const fetchedQuestions = questionsResponse.data || [];
      
      fetchedQuestions.forEach(q => {
        if (activeElementIds.includes(q.element_id)) {
          if (!questionsMap[q.element_id]) {
            questionsMap[q.element_id] = [];
          }
          questionsMap[q.element_id].push(q);
          relevantQuestionIds.add(q.id);
        }
      });
      setAllQuestions(questionsMap);

      // --- Process Scores ---
      const allTeamEvaluations = evaluationsResponse.data || [];
      const relevantEvaluations = allTeamEvaluations.filter(ev => relevantQuestionIds.has(ev.question_id));

      const qScores = {};
      const eScores = {};

      // Calculate Question Scores
      relevantQuestionIds.forEach(qId => {
        const questionEvals = relevantEvaluations.filter(ev => ev.question_id === qId);
        if (questionEvals.length > 0) {
          const sum = questionEvals.reduce((acc, curr) => acc + curr.evaluation_value, 0);
          qScores[qId] = Math.round((sum / questionEvals.length) * 10) / 10;
        } else {
          qScores[qId] = 0;
        }
      });
      setQuestionScores(qScores);

      // Calculate Element Scores
      validElements.forEach(el => {
        const elQuestions = questionsMap[el.id] || [];
        if (elQuestions.length > 0) {
          const elEvaluations = relevantEvaluations.filter(ev => 
             elQuestions.some(q => q.id === ev.question_id)
          );

          if (elEvaluations.length > 0) {
            const sum = elEvaluations.reduce((acc, curr) => acc + curr.evaluation_value, 0);
            eScores[el.id] = Math.round((sum / elEvaluations.length) * 10) / 10;
          } else {
            eScores[el.id] = 0;
          }
        } else {
          eScores[el.id] = 0;
        }
      });
      setElementScores(eScores);

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleElementClick = (element) => {
    setSelectedElement(element);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  return (
    <div className="h-[calc(90vh-64px)] overflow-hidden flex flex-col p-6">
      <div className="mb-6 flex-shrink-0">
        <h1 className="text-2xl font-bold text-brand-dark flex items-center">
          <SafeIcon icon={FiLayout} className="w-7 h-7 mr-3 text-brand-secondary" />
          Team Dashboard
        </h1>
        <p className="text-gray-600 mt-2">
          Übersicht der Team-Bewertungen nach Clustern.
        </p>
      </div>

      {activeElements.length === 0 ? (
        <div className="flex-grow">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <div className="bg-gray-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiTarget} className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-brand-dark mb-2">
              Keine aktiven Elemente
            </h3>
            <p className="text-gray-500 max-w-md mx-auto">
              Es sind momentan keine Elemente für Ihr Team aktiviert. Der Team-Owner muss zuerst Module in der Team-Verwaltung freischalten.
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow flex flex-col flex-grow overflow-hidden">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200 flex-shrink-0">
            <nav className="flex flex-wrap -mb-px px-2" aria-label="Tabs">
              {clusteredElements.map(cluster => (
                <button
                  key={cluster.id}
                  onClick={() => {
                      setActiveClusterId(cluster.id);
                      // Automatisch das erste Element des Clusters auswählen, falls keines ausgewählt ist oder es nicht zum neuen Cluster gehört
                      const firstElement = cluster.elements && cluster.elements.length > 0 ? cluster.elements[0] : null;
                      if (!selectedElement || !cluster.elements.find(e => e.id === selectedElement.id)) {
                          setSelectedElement(firstElement);
                      }
                  }}
                  className={`
                    group inline-flex items-center py-4 px-6 border-b-2 font-medium text-sm whitespace-nowrap transition-colors
                    ${activeClusterId === cluster.id
                      ? 'border-brand-secondary text-brand-secondary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                  `}
                >
                  {cluster.title}
                </button>
              ))}
            </nav>
          </div>

          <div className="flex-grow overflow-hidden p-0">
            <div className="grid grid-cols-1 lg:grid-cols-2 h-full divide-y lg:divide-y-0 lg:divide-x divide-gray-200">
              
              {/* Left Column: Active Chart */}
              <div className="overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent h-full">
                
                {/* Active Cluster Content */}
                {clusteredElements.map((cluster) => {
                  if (cluster.id !== activeClusterId) return null;
                  
                  return (
                    <div key={cluster.id} className="flex flex-col mb-8 animate-fadeIn">
                      <div className="flex items-center justify-between mb-2">
                        <h2 className="text-lg font-semibold text-brand-dark flex items-center">
                          {cluster.title}
                        </h2>
                      </div>
                      {cluster.description && (
                         <p className="text-sm text-gray-500 mb-6">{cluster.description}</p>
                      )}
                      
                      <div className="flex-grow flex items-center justify-center min-h-[350px]">
                        <ElementChart 
                          elements={cluster.elements} 
                          scores={elementScores}
                          onElementClick={handleElementClick}
                          selectedElement={selectedElement}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Right Column: Fixed Detail View */}
              <div className="h-full flex flex-col overflow-hidden bg-white">
                <div className="p-6 border-b border-gray-100 flex-shrink-0 bg-gray-50/30">
                  <h2 className="text-lg font-semibold text-brand-dark">
                    {selectedElement ? (
                      <span className="flex items-center text-brand-secondary">
                        <SafeIcon icon={FiTarget} className="w-5 h-5 mr-2" />
                        {selectedElement.title}
                      </span>
                    ) : 'Detailansicht'}
                  </h2>
                </div>
                
                <div className="flex-grow overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                  {selectedElement ? (
                    <QuestionDetails 
                      element={selectedElement}
                      questions={allQuestions[selectedElement.id]}
                      scores={questionScores}
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400">
                      <SafeIcon icon={FiTarget} className="w-16 h-16 mb-4 opacity-20" />
                      <p className="text-center">Wählen Sie ein Element im Netzdiagramm aus,<br/>um detaillierte Fragen-Auswertungen zu sehen.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;